import { JOBS_BY_ID } from '@renavora/content';
import { resolveStats } from '@renavora/sim';
import {
  SNAPSHOT_SCHEMA_VERSION,
  type JobDefinition,
  type JobType,
  type PublishedSnapshotData,
  type SnapshotEquipmentFit,
} from '@renavora/shared';

import type { SerializedCharacter } from '../persistence/index.js';

// Tier 1 jobs intentionally have no formal Type tags. Phase 5 still needs
// starter snapshots to carry role hints before the first job change, so the
// snapshot builder uses conservative hints until the character reaches a typed
// job.
const TIER1_ROLE_HINTS: Readonly<Record<string, readonly JobType[]>> = {
  entertainer: ['Support'],
  'fortune-teller': ['MagicDPS', 'Support'],
  hunter: ['RangedDPS'],
  'priest-novice': ['Healer', 'Support'],
  thief: ['MeleeDPS'],
  warrior: ['Tank'],
};

export class SnapshotDataBuildError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SnapshotDataBuildError';
  }
}

export function currentSnapshotJobTypes(record: SerializedCharacter): readonly JobType[] {
  return snapshotRoleTypes(currentJob(record));
}

/**
 * Pure builder for the frozen, read-only character snapshot data. This is the
 * single `resolveStats` pipeline shared by the in-battle ally representation
 * (mutual party link freeze + harness) — it carries no publish/discovery
 * concerns. Callers that need publish metadata wrap this in their own envelope.
 */
export function buildSnapshotData(record: SerializedCharacter): PublishedSnapshotData {
  const job = currentJob(record);
  const roleTypes = snapshotRoleTypes(job);
  const jobHistory = record.character.jobHistory.flatMap((entry) => {
    if (entry.jobId === job.id) return [];
    const historyJob = JOBS_BY_ID.get(entry.jobId);
    return historyJob === undefined ? [] : [{ job: historyJob, maxLv: entry.maxLv }];
  });
  return {
    schemaVersion: SNAPSHOT_SCHEMA_VERSION,
    characterId: record.id,
    characterName: record.name,
    currentJobId: job.id,
    currentJobLevel: record.character.jobLevel,
    currentJobTier: job.tier,
    currentJobTypes: roleTypes,
    equipment: record.character.equipment.map((item) => ({
      fit: equipmentFit(item.typeAffinity, roleTypes),
      item,
      slot: item.slot,
    })),
    jobMaxLevels: jobMaxLevels(record, job),
    resolvedStats: resolveStats({
      equipment: record.character.equipment,
      job,
      jobHistory,
      jobLevel: record.character.jobLevel,
    }),
    ultimateSkillId: job.ultimateSkillId,
  };
}

function snapshotRoleTypes(job: JobDefinition): readonly JobType[] {
  if (job.types.length > 0) return job.types;
  return TIER1_ROLE_HINTS[job.id] ?? [];
}

function currentJob(record: SerializedCharacter): JobDefinition {
  const job = JOBS_BY_ID.get(record.character.jobId);
  if (job === undefined) {
    throw new SnapshotDataBuildError(`Unknown current job: ${record.character.jobId}`);
  }
  return job;
}

function jobMaxLevels(
  record: SerializedCharacter,
  current: JobDefinition,
): Readonly<Record<string, number>> {
  const levels = new Map<string, number>();
  for (const entry of record.character.jobHistory) {
    levels.set(entry.jobId, Math.max(levels.get(entry.jobId) ?? 0, entry.maxLv));
  }
  levels.set(current.id, Math.max(levels.get(current.id) ?? 0, record.character.jobLevel));
  return Object.fromEntries([...levels.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

function equipmentFit(
  typeAffinity: readonly JobType[],
  currentJobTypes: readonly JobType[],
): SnapshotEquipmentFit {
  if (typeAffinity.length === 0) return 'unknown';
  const current = new Set(currentJobTypes);
  return typeAffinity.some((type) => current.has(type)) ? 'fit' : 'mismatch';
}
